// src/components/compliance/modals/RecommendationDetailModal.jsx
// Copyright (c) 2025 Nebularis Holdings LLC
// Licensed to Nutrx Solutions under a non-exclusive, non-transferable license.
// See IP License Agreement for usage restrictions.

import React from "react";
import ModalShell from "./shared/ModalShell";
import ModalSection from "./shared/ModalSection";
import ModalActionRow from "./shared/ModalActionRow";
import MetricTile from "./shared/MetricTile";

const CONNECTOR_PROVIDER_DISPLAY_NAMES = {
  aws: "AWS",
  cloudflare: "Cloudflare",
  github: "GitHub",
  google_workspace: "Google Workspace",
  okta: "Okta",
};

function cleanConnectorProvider(value) {
  return String(value || "").trim().toLowerCase();
}

function formatConnectorProvider(value) {
  const normalizedProvider = cleanConnectorProvider(value);

  if (CONNECTOR_PROVIDER_DISPLAY_NAMES[normalizedProvider]) {
    return CONNECTOR_PROVIDER_DISPLAY_NAMES[normalizedProvider];
  }

  return String(value || "Unknown")
    .replace(/_/g, " ")
    .replace(/\b\w/g, (match) => match.toUpperCase());
}

function titleCaseLabel(value, fallback = "—") {
  const text = String(value || "").trim();
  if (!text) return fallback;
  return text
    .replace(/_/g, " ")
    .replace(/-/g, " ")
    .replace(/\b\w/g, (match) => match.toUpperCase());
}

function formatRecommendationPriority(value) {
  const normalized = String(value || "medium").trim().toLowerCase();
  if (normalized === "critical") return "Critical";
  if (normalized === "high") return "High";
  if (normalized === "low") return "Low";
  return "Medium";
}

function priorityColor(value) {
  const normalized = String(value || "").trim().toLowerCase();
  if (normalized === "critical" || normalized === "high") return "#fb7185";
  if (normalized === "medium") return "#fbbf24";
  return "#34d399";
}

function confidenceColor(value) {
  const normalized = String(value || "").trim().toLowerCase();
  if (normalized === "high") return "#34d399";
  if (normalized === "medium") return "#fbbf24";
  return "#bfdbfe";
}

function normalizeList(value) {
  if (!value) return [];
  if (Array.isArray(value)) {
    return value.filter((item) => item !== undefined && item !== null && item !== "");
  }
  return [value].filter((item) => item !== undefined && item !== null && item !== "");
}

function formatList(value, fallback = "—") {
  const items = normalizeList(value)
    .map((item) => String(item).trim())
    .filter(Boolean);

  return items.length ? items.join(", ") : fallback;
}

function normalizeStatus(value) {
  return String(value || "").trim().toLowerCase();
}

const compactGridStyle = {
  display: "grid",
  gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))",
  gap: 14,
};

const metaGridStyle = {
  display: "grid",
  gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
  gap: 12,
};

const labelStyle = {
  color: "#bfdbfe",
  fontSize: 12,
  fontWeight: 800,
  marginBottom: 4,
};

const valueStyle = {
  color: "#f8fafc",
  fontSize: 15,
  fontWeight: 850,
  overflowWrap: "anywhere",
};

const bodyTextStyle = {
  color: "#bfdbfe",
  fontSize: 14,
  lineHeight: 1.6,
  margin: 0,
};

const feedbackNoticeStyle = {
  border: "1px solid rgba(96, 165, 250, 0.35)",
  borderRadius: 14,
  padding: "10px 12px",
  background: "rgba(37, 99, 235, 0.12)",
  color: "#bfdbfe",
  fontSize: 13,
  lineHeight: 1.45,
};

const acceptedNoticeStyle = {
  ...feedbackNoticeStyle,
  border: "1px solid rgba(52, 211, 153, 0.38)",
  background: "rgba(6, 78, 59, 0.22)",
  color: "#bbf7d0",
};

const acceptedStatusChipStyle = {
  justifySelf: "start",
  border: "1px solid rgba(52, 211, 153, 0.42)",
  borderRadius: 999,
  padding: "7px 12px",
  background: "rgba(6, 78, 59, 0.24)",
  color: "#bbf7d0",
  fontSize: 13,
  fontWeight: 900,
};

const feedbackErrorStyle = {
  ...feedbackNoticeStyle,
  border: "1px solid rgba(248, 113, 113, 0.38)",
  background: "rgba(127, 29, 29, 0.20)",
  color: "#fecaca",
};

export default function RecommendationDetailModal({
  recommendation,
  feedbackState = {},
  onClose,
  onOpenReviewPage,
  onSubmitFeedback,
}) {
  if (!recommendation) return null;

  const status = normalizeStatus(recommendation.status);
  const isAccepted = status === "accepted";
  const priorityLabel = formatRecommendationPriority(recommendation.priority);
  const confidenceLabel = recommendation.confidence_level || "—";
  const reason =
    recommendation.reasoning_summary ||
    recommendation.detail ||
    recommendation.reason ||
    recommendation.summary ||
    "OASYS ranked this item from current Mission Control context.";

  const nextSteps = normalizeList(recommendation.next_steps);
  const feedbackLoadingType = feedbackState.loadingType || null;
  const feedbackDisabled = Boolean(feedbackLoadingType) || !recommendation.id || isAccepted;

  const buildFeedbackAction = (key, label, className = "btn btn-ghost btn-sm") => ({
    key,
    label: feedbackLoadingType === key ? "Saving…" : label,
    className,
    disabled: feedbackDisabled,
    onClick: () => onSubmitFeedback?.(key),
  });

  const acceptDisabled = Boolean(feedbackLoadingType) || !recommendation.id || isAccepted;

  return (
    <ModalShell
      open={Boolean(recommendation)}
      ariaLabel="Recommendation details"
      eyebrow="OASYS · Recommendation Detail"
      title={recommendation.title || "Recommended Action"}
      subtitle="Advisory guidance only. OASYS is not executing changes, closing tasks, approving evidence, or running validation from this modal."
      onClose={onClose}
      maxWidth="980px"
      accentBorder="rgba(96, 165, 250, 0.52)"
      zIndex={160}
    >
      <div style={compactGridStyle}>
        <MetricTile
          label="Priority"
          value={priorityLabel}
          sub="ranked recommendation"
          color={priorityColor(recommendation.priority)}
        />
        <MetricTile
          label="Score"
          value={recommendation.total_score || "—"}
          sub="recommendation score"
          color={priorityColor(recommendation.priority)}
        />
        <MetricTile
          label="Confidence"
          value={confidenceLabel}
          sub={recommendation.confidence_score ? `score ${recommendation.confidence_score}` : "confidence level"}
          color={confidenceColor(recommendation.confidence_level)}
        />
        <MetricTile
          label="Status"
          value={isAccepted ? "Accepted" : titleCaseLabel(recommendation.status || "active")}
          sub={isAccepted ? "operator intent recorded" : "current recommendation state"}
          color={isAccepted ? "#34d399" : "#bfdbfe"}
        />
      </div>

      <ModalSection title="Why OASYS recommends this">
        <p style={bodyTextStyle}>{reason}</p>
      </ModalSection>

      <ModalSection title="Recommendation scope">
        <div style={metaGridStyle}>
          <div>
            <div style={labelStyle}>Framework</div>
            <div style={valueStyle}>{recommendation.affected_framework || "—"}</div>
          </div>
          <div>
            <div style={labelStyle}>Connector</div>
            <div style={valueStyle}>
              {recommendation.affected_connector
                ? formatConnectorProvider(recommendation.affected_connector)
                : "—"}
            </div>
          </div>
          <div>
            <div style={labelStyle}>Category</div>
            <div style={valueStyle}>
              {titleCaseLabel(recommendation.category || recommendation.recommendation_type)}
            </div>
          </div>
          <div>
            <div style={labelStyle}>Surface</div>
            <div style={valueStyle}>
              {titleCaseLabel(recommendation.surface || "mission_control")}
            </div>
          </div>
        </div>
      </ModalSection>

      <ModalSection title="Affected context">
        <div style={{ display: "grid", gap: 12 }}>
          <div>
            <div style={labelStyle}>Affected Controls</div>
            <div style={valueStyle}>{formatList(recommendation.affected_controls)}</div>
          </div>
          <div>
            <div style={labelStyle}>Affected Findings</div>
            <div style={valueStyle}>{formatList(recommendation.affected_findings)}</div>
          </div>
          <div>
            <div style={labelStyle}>Affected Resources</div>
            <div style={valueStyle}>{formatList(recommendation.affected_resources)}</div>
          </div>
        </div>
      </ModalSection>

      {recommendation.suggested_action ? (
        <ModalSection title="Suggested action">
          <p style={bodyTextStyle}>{recommendation.suggested_action}</p>
        </ModalSection>
      ) : null}

      {nextSteps.length > 0 ? (
        <ModalSection title="Next steps">
          <ol style={{ margin: 0, paddingLeft: 22, color: "#bfdbfe", lineHeight: 1.7 }}>
            {nextSteps.map((step, index) => (
              <li key={`${String(step)}-${index}`}>{String(step)}</li>
            ))}
          </ol>
        </ModalSection>
      ) : null}

      <ModalSection
        title="Operator feedback"
        description={
          isAccepted
            ? "Feedback is locked because this recommendation has already been accepted."
            : "Feedback tunes the recommendation signal. It does not execute remediation or change control state."
        }
        badge={isAccepted ? "Locked" : undefined}
        badgeTone={isAccepted ? "default" : "default"}
      >
        <div style={{ display: "grid", gap: 10 }}>
          {!isAccepted && feedbackState.message ? (
            <div style={feedbackNoticeStyle}>{feedbackState.message}</div>
          ) : null}
          {feedbackState.error ? <div style={feedbackErrorStyle}>{feedbackState.error}</div> : null}
          {!recommendation.id ? (
            <div style={feedbackErrorStyle}>
              Feedback is unavailable until this recommendation is persisted.
            </div>
          ) : null}

          {isAccepted ? (
            <div style={feedbackNoticeStyle}>
              Signal feedback is closed for this recommendation because operator intent has already been recorded.
            </div>
          ) : (
            <ModalActionRow
              actions={[
                buildFeedbackAction("useful", "Useful"),
                buildFeedbackAction("not_useful", "Not useful"),
                buildFeedbackAction("dismissed", "Dismiss", "btn btn-ghost btn-sm"),
              ]}
            />
          )}
        </div>
      </ModalSection>

      <ModalSection
        title="Operator decision"
        description={
          isAccepted
            ? "Operator intent has been recorded. This recommendation is ready for a future workflow handoff."
            : "Accept records that an operator agrees this recommendation should become work in a future workflow. It does not execute the work."
        }
        badge={isAccepted ? "Accepted" : "Advisory"}
        badgeTone={isAccepted ? "low" : "medium"}
      >
        <div style={{ display: "grid", gap: 10 }}>
          {isAccepted ? (
            <div style={acceptedNoticeStyle}>
              Recommendation accepted. OASYS recorded operator intent only. No remediation, validation, evidence approval, task closure, or workflow execution was performed.
            </div>
          ) : (
            <p style={bodyTextStyle}>
              Accepting this recommendation records intent only. OASYS will not remediate the source system, close a task, approve evidence, run validation, or advance a workflow.
            </p>
          )}

          {isAccepted ? (
            <span style={acceptedStatusChipStyle}>Accepted</span>
          ) : (
            <ModalActionRow
              actions={[
                {
                  key: "accepted",
                  label: feedbackLoadingType === "accepted" ? "Accepting…" : "Accept recommendation",
                  className: "btn btn-primary btn-sm",
                  disabled: acceptDisabled,
                  onClick: () => onSubmitFeedback?.("accepted"),
                },
              ]}
            />
          )}
        </div>
      </ModalSection>

      <ModalActionRow
        actions={[
          {
            key: "close",
            label: "Close",
            className: "btn btn-ghost btn-sm",
            onClick: onClose,
          },
          {
            key: "open-review",
            label: "Open Review Page",
            className: "btn btn-primary btn-sm",
            onClick: onOpenReviewPage,
          },
        ]}
      />
    </ModalShell>
  );
}
